import { inject, injectable } from "@launchtray/tsyringe-async";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { SceneManager } from "../../../../scene/SceneManager";
import { PromiseCompletionSource } from "../../../../shared/async/PromiseCompletionSource";
import { PersonalOfferReason } from "../../../networking/data/responses/personaloffer/PersonalOfferReason";
import { SceneIndex } from "../../SceneIndex";
import { AsyncOperationNotification } from "../../common/components/AsyncOperationNotification";
import { IFirstTimeExperienceModel } from "../../common/models/IFirstTimeExperienceModel";
import { IPlayerModel } from "../../common/models/IPlayerModel";
import { IRenterPolicyModel } from "../../common/models/Renters/IRenterPolicyModel";
import { AmazingWinCoinsPopupScene } from "../../popups/awazingwincoins/AmazingWinCoinsPopupScene";
import { CheckOutFixUpgradePopupScene } from "../../popups/checkoutfixupgrade/CheckOutFixUpgradePopupScene";
import { CheckOutShopPopupScene } from "../../popups/checkoutshop/CheckOutShopPopupScene";
import { CookiesPopupScene } from "../../popups/cookies/CookiesPopupScene";
import { DiceRollsOfferPopupScene } from "../../popups/dicerollsoffer/DiceRollsOfferPopupScene";
import { IDiceRollsOfferModel } from "../../popups/dicerollsoffer/model/IDiceRollsOfferModel";
import { GiftBoxPopupScene } from "../../popups/gift/GiftBoxPopupScene";
import { MediumWinCoinsPopupScene } from "../../popups/mediumlwincoins/MediumWinCoinsPopupScene";
import { PushPopupScene } from "../../popups/push/PushPopupScene";
import { RaidPopupScene } from "../../popups/raid/RaidPopupScene";
import { RenterPolicyPopupScene } from "../../popups/renterpolicy/RenterPolicyPopupScene";
import { RenterPolicyType } from "../../popups/renterpolicy/components/RenterPolicyType";
import { SmallWinCoinsPopupScene } from "../../popups/smallwincoins/SmallWinCoinsPopupScene";
import { SocialCommunitiesPopupScene } from "../../popups/socialcommunities/socialcommunities/SocialCommunitiesPopupScene";
import { SocialCommunitiesIntroPopupScene } from "../../popups/socialcommunities/socialcommunitiesintro/SocialCommunitiesIntroPopupScene";
import { StealPopupScene } from "../../popups/steal/StealPopupScene";
import { VisitPopupScene } from "../../popups/visit/VisitPopupScene";
import { DiceRollAction } from "../flow/impl/DiceRollAction";
import { DialogData } from "../flow/impl/dialogAction/DialogData";
import { PushDialogData } from "../flow/impl/dialogAction/PushDialogData";
import { AmazingWinAction } from "../flow/impl/diceActions/AmazingWinAction";
import { DeadRollAction } from "../flow/impl/diceActions/DeadRollAction";
import { DeadRollMediumCoinsAction } from "../flow/impl/diceActions/DeadRollMediumCoinsAction";
import { DeadRollSmallCoinsAction } from "../flow/impl/diceActions/DeadRollSmallCoinsAction";
import { DiceComboAction } from "../flow/impl/diceActions/DiceComboAction";
import { RaidAction } from "../flow/impl/diceActions/raidAction/RaidAction";
import { StealAction } from "../flow/impl/diceActions/stealAction/StealAction";
import { VisitAction } from "../flow/impl/diceActions/visitAction/VisitAction";
import { IGameModel } from "../models/IGameModel";
import { IGameViewModel } from "../models/IGameViewModel";
import { IGameController } from "./IGameController";
import { PiggyPopupScene } from "../../popups/piggy/PiggyPopupScene";
import { CheckoutPiggyPopupScene } from "../../popups/checkoutpiggy/CheckoutPiggyPopupScene";
import { CurrentPiggyState } from "../../popups/piggy/components/piggycontentview/CurrentPiggyContentView";
import { IContextResolver } from "../../../composition/IContextResolver";
import { LoginModule } from "../../popups/login/LoginModule";
import { VariableSaver } from "../../../../tools/variableSaver/VariableSaver";
import { AuthConstants } from "../../popups/login/AuthConstants";
import { IAuthModel } from "../../popups/login/models/IAuthModel";
import { MyWalletModule } from "../../popups/myWallet/MyWalletModule";
import { NotificationMessagesModule } from "../../popups/notificationMessages/NotificationMessagesModule";
import { NotificationMessageType } from "../../popups/notificationMessages/NotificationMessageType";
import { IConnectionModel } from "by/gamefactory/cityversetycoon/networking/IConnectionModel";
import { MyBuildingsModule } from "../../popups/mybuildings/MyBuildingsModule";
import { HourlyBonusState } from "../../popups/hourlyBonus/mainHourlyBonus/HourlyBonusState";
import { HourlyBonusModule } from "../../popups/hourlyBonus/HourlyBonusModule";
import { HourlyBonusExecutionOptions, HourlyBonusType } from "../../popups/hourlyBonus/HourlyBonusExecutionOptions";
import { MarketplaceModule } from "../../popups/marketplace/MarketplaceModule";
import { LatestNewsModule } from "../../popups/latestNews/LatestNewsModule";
import { RenterQuestIntroScene } from "../../popups/renterquestintro/RenterQuestIntroScene";
import { WalletTabType } from "../../popups/myWallet/components/WalletTabType";
import { GiftAction } from "../flow/impl/diceActions/GiftAction";
import { BuildingRightsType } from "by/gamefactory/cityversetycoon/networking/data/responses/user/BuildingRightsType";
import { Redirector } from "../../../../tools/Redirector";
import { LeaderboardIntroScene } from "../../popups/leaderboardintro/LeaderboardIntroScene";
import { GiftDiceRollsAction } from "../flow/impl/diceActions/GiftDiceRollsAction";
import { GiftShieldsAction } from "../flow/impl/diceActions/GiftShieldsAction";
import { GiftPiggyWheelAction } from "../flow/impl/diceActions/GiftPiggyWheelAction";

@injectable()
export class GameController implements IGameController {
    private readonly gameModel: IGameModel;
    private readonly gameViewModel: IGameViewModel;
    private readonly playerModel: IPlayerModel;
    private readonly authModel: IAuthModel;
    private readonly contextResolver: IContextResolver;
    private readonly sceneManager: SceneManager;
    private readonly diceRollsOfferModel: IDiceRollsOfferModel;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly firstTimeExperienceModel: IFirstTimeExperienceModel;
    private readonly renterPolicyModel: IRenterPolicyModel;

    private readonly raidPopupSceneFactory: (action: RaidAction) => Promise<RaidPopupScene>;
    private readonly smallWinCoinsPopupSceneFactory: (action: DeadRollSmallCoinsAction) => Promise<SmallWinCoinsPopupScene>;
    private readonly mediumWinCoinsPopupSceneFactory: (action: DeadRollMediumCoinsAction) => Promise<MediumWinCoinsPopupScene>;
    private readonly visitPopupSceneFactory: (action: VisitAction) => Promise<VisitPopupScene>;
    private readonly amazingWinCoinsPopupSceneFactory: (action: AmazingWinAction) => Promise<AmazingWinCoinsPopupScene>;
    private readonly giftBoxPopupSceneFactory: (diceComboAction: DiceComboAction) => Promise<GiftBoxPopupScene>;
    private readonly stealPopupSceneFactory: (action: StealAction) => Promise<StealPopupScene>;
    private readonly cookiesPopupSceneFactory: () => Promise<CookiesPopupScene>;
    private readonly pushPopupSceneFactory: (dialogData: PushDialogData) => Promise<PushPopupScene>;
    private readonly checkoutShopSceneFactory: () => Promise<CheckOutShopPopupScene>;
    private readonly diceRollsOfferSceneFactory: (reason: string) => Promise<DiceRollsOfferPopupScene>;
    private readonly checkoutFixUpgradeSceneFactory: () => Promise<CheckOutFixUpgradePopupScene>;
    private readonly socialIntroFactory: () => Promise<SocialCommunitiesIntroPopupScene>;
    private readonly socialFactory: () => Promise<SocialCommunitiesPopupScene>;
    private readonly renterPolicyFactory: (renterPolicyType: RenterPolicyType) => Promise<RenterPolicyPopupScene>;
    private readonly piggyFactory: (state: CurrentPiggyState) => Promise<PiggyPopupScene>;
    private readonly checkoutPiggyFactory: () => Promise<CheckoutPiggyPopupScene>;
    private readonly renterQuestIntroFactory: () => Promise<RenterQuestIntroScene>;
    private readonly leaderboardIntroFactory: () => Promise<LeaderboardIntroScene>;

    private readonly connectionModel: IConnectionModel;
    private readonly variableSaver: VariableSaver;
    private readonly logger: ILogger;

    private loginShowed: boolean = false;

    public constructor(
        @inject("IGameModel") gameModel: IGameModel,
        @inject("IGameViewModel") gameViewModel: IGameViewModel,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        @inject("IAuthModel") authModel: IAuthModel,
        @inject("IDiceRollsOfferModel") diceRollsOfferModel: IDiceRollsOfferModel,
        @inject("IFirstTimeExperienceModel") firstTimeExperienceModel: IFirstTimeExperienceModel,
        @inject("IRenterPolicyModel") renterPolicyModel: IRenterPolicyModel,
        asyncOperationNotification: AsyncOperationNotification,
        @inject("IContextResolver") contextResolver: IContextResolver,
        sceneManager: SceneManager,
        @inject("RaidPopupSceneFactory") raidPopupSceneFactory: (action: RaidAction) => Promise<RaidPopupScene>,
        @inject("SmallWinCoinsPopupSceneFactory") smallWinCoinsPopupSceneFactory: (action: DeadRollSmallCoinsAction) => Promise<SmallWinCoinsPopupScene>,
        @inject("MediumWinCoinsPopupSceneFactory") mediumWinCoinsPopupSceneFactory: (action: DeadRollMediumCoinsAction) => Promise<MediumWinCoinsPopupScene>,
        @inject("AmazingWinCoinsPopupSceneFactory") amazingWinCoinsPopupSceneFactory: (action: AmazingWinAction) => Promise<AmazingWinCoinsPopupScene>,
        @inject("VisitPopupSceneFactory") visitPopupSceneFactory: (action: VisitAction) => Promise<VisitPopupScene>,
        @inject("GiftBoxPopupSceneFactory") giftBoxPopupSceneFactory: (action: DiceComboAction) => Promise<GiftBoxPopupScene>,
        @inject("StealPopupSceneFactory") stealPopupSceneFactory: (action: StealAction) => Promise<StealPopupScene>,
        @inject("CookiesPopupSceneFactory") cookiesPopupSceneFactory: () => Promise<CookiesPopupScene>,
        @inject("PushPopupSceneFactory") pushPopupSceneFactory: (dialogData: PushDialogData) => Promise<PushPopupScene>,
        @inject("CheckOutShopPopupSceneFactory") checkoutShopSceneFactory: () => Promise<CheckOutShopPopupScene>,
        @inject("DiceRollsOfferPopupSceneFactory") diceRollsOfferSceneFactory: (reason: string) => Promise<DiceRollsOfferPopupScene>,
        @inject("CheckOutFixUpgradePopupSceneFactory") checkoutFixUpgradeSceneFactory: () => Promise<CheckOutFixUpgradePopupScene>,
        @inject("SocialIntroPopupSceneFactory") socialIntroFactory: () => Promise<SocialCommunitiesIntroPopupScene>,
        @inject("SocialPopupSceneFactory") socialFactory: () => Promise<SocialCommunitiesPopupScene>,
        @inject("RenterPolicyPopupSceneFactory") renterPolicyFactory: (renterPolicyType: RenterPolicyType) => Promise<RenterPolicyPopupScene>,
        @inject("PiggyPopupSceneFactory") piggyFactory: (state: CurrentPiggyState) => Promise<PiggyPopupScene>,
        @inject("CheckoutPiggyPopupSceneFactory") checkoutPiggyFactory: () => Promise<CheckoutPiggyPopupScene>,
        @inject("RenterQuestIntroSceneFactory") renterQuestIntroFactory: () => Promise<RenterQuestIntroScene>,
        @inject("LeaderboardIntroSceneFactory") leaderboardIntroFactory: () => Promise<LeaderboardIntroScene>,
        @inject("IConnectionModel") connectionModel: IConnectionModel,
        variableSaver: VariableSaver,
        @inject("ILogger") logger: ILogger
    ) {
        this.gameModel = gameModel;
        this.gameViewModel = gameViewModel;
        this.playerModel = playerModel;
        this.authModel = authModel;
        this.diceRollsOfferModel = diceRollsOfferModel;
        this.contextResolver = contextResolver;
        this.sceneManager = sceneManager;
        this.asyncOperationNotification = asyncOperationNotification;
        this.firstTimeExperienceModel = firstTimeExperienceModel;
        this.renterPolicyModel = renterPolicyModel;
        this.raidPopupSceneFactory = raidPopupSceneFactory;
        this.smallWinCoinsPopupSceneFactory = smallWinCoinsPopupSceneFactory;
        this.mediumWinCoinsPopupSceneFactory = mediumWinCoinsPopupSceneFactory;
        this.amazingWinCoinsPopupSceneFactory = amazingWinCoinsPopupSceneFactory;
        this.visitPopupSceneFactory = visitPopupSceneFactory;
        this.stealPopupSceneFactory = stealPopupSceneFactory;
        this.giftBoxPopupSceneFactory = giftBoxPopupSceneFactory;
        this.cookiesPopupSceneFactory = cookiesPopupSceneFactory;
        this.pushPopupSceneFactory = pushPopupSceneFactory;
        this.checkoutShopSceneFactory = checkoutShopSceneFactory;
        this.diceRollsOfferSceneFactory = diceRollsOfferSceneFactory;
        this.checkoutFixUpgradeSceneFactory = checkoutFixUpgradeSceneFactory;
        this.socialIntroFactory = socialIntroFactory;
        this.socialFactory = socialFactory;
        this.renterPolicyFactory = renterPolicyFactory;
        this.piggyFactory = piggyFactory;
        this.checkoutPiggyFactory = checkoutPiggyFactory;
        this.renterQuestIntroFactory = renterQuestIntroFactory;
        this.leaderboardIntroFactory = leaderboardIntroFactory;
        this.connectionModel = connectionModel;
        this.variableSaver = variableSaver;
        this.logger = logger;
    }

    public subtractRolls(): void {
        this.playerModel.subtractRolls();
    }

    public async showRaidPopup(action: RaidAction): Promise<void> {
        const scene = await this.asyncOperationNotification.autoShowHide(this.raidPopupSceneFactory(action));
        await this.sceneManager.showViewableAsync(scene);
    }

    public async showSmallWinCoinsPopup(action: DeadRollSmallCoinsAction): Promise<void> {
        const scene = await this.asyncOperationNotification.autoShowHide(this.smallWinCoinsPopupSceneFactory(action));
        await this.sceneManager.showViewableAsync(scene);
    }

    public async showMediumWinCoinsPopup(action: DeadRollMediumCoinsAction): Promise<void> {
        const scene = await this.asyncOperationNotification.autoShowHide(this.mediumWinCoinsPopupSceneFactory(action));
        await this.sceneManager.showViewableAsync(scene);
    }

    public async showAmazingWinCoinsPopup(action: AmazingWinAction): Promise<void> {
        const scene = await this.asyncOperationNotification.autoShowHide(this.amazingWinCoinsPopupSceneFactory(action));
        await this.sceneManager.showViewableAsync(scene);
    }

    public async showVisitPopup(action: VisitAction): Promise<void> {
        const scene = await this.asyncOperationNotification.autoShowHide(this.visitPopupSceneFactory(action));
        await this.sceneManager.showViewableAsync(scene);
    }

    public async showGiftBoxPopup(action: DiceComboAction): Promise<void> {
        const scene = await this.asyncOperationNotification.autoShowHide(this.giftBoxPopupSceneFactory(action));
        await this.sceneManager.showViewableAsync(scene);
    }

    public async showStealPopup(action: StealAction): Promise<void> {
        const scene = await this.asyncOperationNotification.autoShowHide(this.stealPopupSceneFactory(action));
        await this.sceneManager.showViewableAsync(scene);
    }

    public async showMyBuildingsPopup(): Promise<boolean> {
        try {
            this.logger.debug(`[BUILDINGS WORKFLOW] Show my buildings`);
            this.gameViewModel.popupShown();
            const module = await this.asyncOperationNotification.autoShowHide(this.contextResolver.resolve(MyBuildingsModule));
            const result = await module.start();
            return result;
        }
        catch (e: unknown) {
            this.logger.error("Exception on executing MyBuildingModule: ", e);
            return false;
        }
        finally {
            this.gameViewModel.popupHidden();
        }
    }

    public async showCheckoutHourlyBonusPopup(isWheel: boolean): Promise<void> {
        await this.showHourlyBonus({ type: HourlyBonusType.CHECKOUT, args: { isWheel } });
    }

    public async showHourlyBonusPopup(state: HourlyBonusState): Promise<void> {
        await this.showHourlyBonus({ type: HourlyBonusType.MAIN, args: state });
    }

    public async showMarketPlacePopupScene(): Promise<void> {
        try {
            this.gameViewModel.popupShown();
            const module = await this.asyncOperationNotification.autoShowHide(this.contextResolver.resolve(MarketplaceModule));
            await module.start(-1);
        }
        catch (e: unknown) {
            this.logger.error("Exception on executing MarketplaceModule: ", e);
        }
        finally {
            this.gameViewModel.popupHidden();
        }
    }

    public async showCookiesPopup(): Promise<void> {
        const scene = await this.asyncOperationNotification.autoShowHide(this.cookiesPopupSceneFactory());
        await this.sceneManager.showViewableAsync(scene);
    }

    public async showPushPopup(dialogData: PushDialogData): Promise<void> {
        const scene = await this.asyncOperationNotification.autoShowHide(this.pushPopupSceneFactory(dialogData));
        await this.sceneManager.showViewableAsync(scene);
    }

    public async showCheckoutShopPopup(): Promise<void> {
        const scene = await this.asyncOperationNotification.autoShowHide(this.checkoutShopSceneFactory());
        await this.sceneManager.showViewableAsync(scene);
    }

    public async showSocialIntroPopup(): Promise<void> {
        const scene = await this.asyncOperationNotification.autoShowHide(this.socialIntroFactory());
        await this.sceneManager.showViewableAsync(scene);
    }

    public async showSocialPopup(): Promise<void> {
        const scene = await this.asyncOperationNotification.autoShowHide(this.socialFactory());
        await this.sceneManager.showViewableAsync(scene);
    }

    public async showNewsPopup(): Promise<void> {
        const module = await this.asyncOperationNotification.autoShowHide(this.contextResolver.resolve(LatestNewsModule));
        await module.start();
    }

    public async showPiggyPopup(state: CurrentPiggyState): Promise<void> {
        this.popupShown();
        const scene = await this.asyncOperationNotification.autoShowHide(this.piggyFactory(state));
        await this.sceneManager.showViewableAsync(scene);
        this.popupHidden();
    }

    private async showCheckoutPiggyPopup(): Promise<void> {
        const scene = await this.asyncOperationNotification.autoShowHide(this.checkoutPiggyFactory());
        await this.sceneManager.showViewableAsync(scene);
    }

    private async showRenterQuestIntroPopup(): Promise<void> {
        await this.sceneManager.showViewableAsync(await this.renterQuestIntroFactory());
    }

    private async showLeaderboardIntroPopup(): Promise<void> {
        await this.sceneManager.showViewableAsync(await this.leaderboardIntroFactory());
    }

    public async showDiceRollsOfferPopup(reason: PersonalOfferReason): Promise<void> {
        this.gameViewModel.popupShown();
        this.asyncOperationNotification.show();
        const result = await this.diceRollsOfferModel.getOfferDto(reason);
        this.asyncOperationNotification.hide();
        if (result) {
            const scene = await this.asyncOperationNotification.autoShowHide(this.diceRollsOfferSceneFactory(reason));
            await this.sceneManager.showViewableAsync(scene);
        }
        this.gameViewModel.popupHidden();
    }

    public async showCheckoutFixUpgradePopup(): Promise<void> {
        const scene = await this.asyncOperationNotification.autoShowHide(this.checkoutFixUpgradeSceneFactory());
        await this.sceneManager.showViewableAsync(scene);
    }

    public async showRenterPolicyPopup(renterPolicyType: RenterPolicyType): Promise<void> {
        this.gameViewModel.popupShown();
        const scene = await this.asyncOperationNotification.autoShowHide(this.renterPolicyFactory(renterPolicyType));
        await this.sceneManager.showViewableAsync(scene);
        this.gameViewModel.popupHidden();
    }

    public async showMainLoginPopup(): Promise<void> {
        this.popupShown();
        if (this.loginShowed) {
            return;
        }
        this.loginShowed = true;
        const loginModule = await this.asyncOperationNotification.autoShowHide(this.contextResolver.resolve(LoginModule));
        await loginModule.start();
        this.loginShowed = false;
        if (this.playerModel.buildingRights.value === BuildingRightsType.RENTER) {
            await this.showOwnerNotificationPopup(NotificationMessageType.UNBOARDING_RENTER);
        }
        if (this.playerModel.buildingRights.value === BuildingRightsType.OWNER && this.firstTimeExperienceModel.becameOwner.value) {
            await this.showOwnerNotificationPopup(NotificationMessageType.UNBOARDING_OWNER);
        }
        this.popupHidden();
    }

    public async showOwnerNotificationPopup(ownerNotificationType: NotificationMessageType): Promise<void> {
        this.popupShown();
        const ownerNotificationModule = await this.asyncOperationNotification.autoShowHide(this.contextResolver.resolve(NotificationMessagesModule));
        await ownerNotificationModule.start(ownerNotificationType);
        this.popupHidden();
    }

    public async showMyWalletPopup(forceTab?: WalletTabType): Promise<void> {
        this.popupShown();
        const myWalletModule = await this.asyncOperationNotification.autoShowHide(this.contextResolver.resolve(MyWalletModule));
        await myWalletModule.start(forceTab);
        this.popupHidden();
    }

    public async showPopupBySceneIndex(sceneIndex: SceneIndex): Promise<void> {
        this.gameViewModel.popupShown();
        switch (sceneIndex) {
            case SceneIndex.MY_BUILDINGS:
                await this.showMyBuildingsPopup();
                break;
            case SceneIndex.COOKIES:
                await this.showCookiesPopup();
                break;
            case SceneIndex.ALLOW_NOTIFICATIONS:
                await this.showPushPopup(new PushDialogData(0));
                break;
            case SceneIndex.CHECKOUT_FIX_UPGRADE:
                await this.showCheckoutFixUpgradePopup();
                break;
            case SceneIndex.SOCIAL:
                await this.showSocialPopup();
                break;
            case SceneIndex.NEWS:
                await this.showNewsPopup();
                break;
            case SceneIndex.PIGGY:
                await this.showPiggyPopup(CurrentPiggyState.LOCKED);
                break;
            case SceneIndex.CHECKOUT_PIGGY:
                await this.showCheckoutPiggyPopup();
                break;
            default:
                this.logger.warn(`Unable to show popup with index: ${sceneIndex}`);
        }
        this.firstTimeExperienceModel.getFirstTimeExperience();
        this.gameViewModel.popupHidden();
    }

    public async showPopupByDialogAction(sceneIndex: SceneIndex, dialogData: DialogData): Promise<void> {
        this.gameViewModel.popupShown();
        switch (sceneIndex) {
            case SceneIndex.ADD_TO_HOME_SCREEN:
                await this.claimAction();
                break;
            case SceneIndex.TUTORIAL:
                await this.claimAction();
                break;
            case SceneIndex.MY_BUILDINGS:
                await this.showMyBuildingsPopup();
                break;
            case SceneIndex.COOKIES:
                await this.showCookiesPopup();
                break;
            case SceneIndex.ALLOW_NOTIFICATIONS:
                await this.claimAction();
                await this.showPushPopup(dialogData as PushDialogData);
                break;
            case SceneIndex.STORE_INTRODUCTION:
                await this.showCheckoutShopPopup();
                await this.claimAction();
                break;
            case SceneIndex.SOCIAL_COMMUNITIES:
                await this.showSocialIntroPopup();
                await this.claimAction();
                break;
            case SceneIndex.TOURNAMENT_INTRODUCTION:
                await this.showOwnerNotificationPopup(NotificationMessageType.TOURNAMENT_INTRO);
                await this.claimAction();
                break;
            case SceneIndex.RENTER_POLICY_INTRODUCTION:
                await this.claimAction();
                await this.showRenterQuestIntroPopup();
                break;
            case SceneIndex.LEADERBOARD_INTRODUCTION:
                await this.showLeaderboardIntroPopup();
                await this.claimAction();
                break;
            default:
                this.logger.warn(`Unable to show popup with index: ${sceneIndex}, data: ${dialogData}`);
        }
        this.gameViewModel.popupHidden();
        this.firstTimeExperienceModel.getFirstTimeExperience();
        await this.onDiceRollsOver();
    }

    public async showDiceResultPopup(action: DiceRollAction, popupCloseTCS: PromiseCompletionSource<void>): Promise<void> {
        let showingMethod: () => Promise<void>;
        switch (action.diceComboAction.constructor) {
            case DeadRollSmallCoinsAction:
                showingMethod = this.showSmallWinCoinsPopup.bind(this, action.diceComboAction);
                break;
            case DeadRollMediumCoinsAction:
                showingMethod = this.showMediumWinCoinsPopup.bind(this, action.diceComboAction);
                break;
            case AmazingWinAction:
                showingMethod = this.showAmazingWinCoinsPopup.bind(this, action.diceComboAction);
                break;
            case GiftDiceRollsAction:
            case GiftShieldsAction:
            case GiftPiggyWheelAction:
                showingMethod = this.showGiftBoxPopup.bind(this, action.diceComboAction);
                break;
            case GiftAction:
                showingMethod = this.showGiftBoxPopup.bind(this, action.diceComboAction);
                break;
            case RaidAction:
                showingMethod = this.showRaidPopup.bind(this, action.diceComboAction);
                break;
            case VisitAction:
                showingMethod = this.showVisitPopup.bind(this, action.diceComboAction);
                break;
            case StealAction:
                showingMethod = this.showStealPopup.bind(this, action.diceComboAction);
                break;
            case DeadRollAction:
                showingMethod = () => Promise.resolve();
                break;
            default:
                this.logger.warn(`Popup ${action.diceComboAction.constructor.name} not yet implemented.`)
                showingMethod = () => Promise.resolve();
        }
        this.gameViewModel.popupShown();
        await showingMethod();
        await this.claimAction();
        this.gameViewModel.popupHidden();
        this.firstTimeExperienceModel.getFirstTimeExperience();
        await this.onDiceRollsOver();
        popupCloseTCS.complete();
    }

    private async showHourlyBonus<T extends HourlyBonusType>(options?: HourlyBonusExecutionOptions<T>): Promise<void> {
        try {
            this.gameViewModel.popupShown();
            const module = await this.asyncOperationNotification.autoShowHide(this.contextResolver.resolve(HourlyBonusModule));
            await module.start(options);
        }
        catch (e: unknown) {
            this.logger.error("Exception on executing HourlyBonusModule: ", e);
        }
        finally {
            this.gameViewModel.popupHidden();
        }
    }

    public showTournament(): void {
        Redirector.openPageInNewTab(`${location.origin}/leaderboard/index.html?${new Date().getTime()}`);
    }

    private async onDiceRollsOver(): Promise<void> {
        if (this.playerModel.rolls.value === 0) {
            await this.showDiceRollsOfferPopup(PersonalOfferReason.ZERO_DICE_ROLLS);
        }
    }

    public async claimAction(): Promise<void> {
        await this.gameModel.claimAction();
    }

    public userActive(): void {
        this.gameViewModel.userActive();
    }

    public popupShown(): void {
        this.gameViewModel.popupShown();
    }

    public popupHidden(): void {
        this.gameViewModel.popupHidden();
    }

    public async getRenterPolicyStatus(): Promise<void> {
        await this.renterPolicyModel.updateRenterPolicyStatus();
    }

    public async reconnect(): Promise<void> {
        try {
            if (!this.connectionModel.connected.value) {
                await this.showMainLoginPopup();
            }
            if (await this.authModel.refreshToken()) {
                await this.authModel.updateAccountProgress();
                return;
            }
            await this.showMainLoginPopup();
        }
        catch (e: unknown) {
            this.logger.debug(`Exception on update auth state: ${e}`);
            if (!this.connectionModel.connected.value) {
                await this.showMainLoginPopup();
            }
        }
    }

    public async firstShowMainLoginPopup(): Promise<void> {
        try {
            if (!this.variableSaver.exist(AuthConstants.WITH_PROGRESS) && !this.playerModel.isAuthorized) {
                this.gameViewModel.popupShown();
                await this.showMainLoginPopup();
                this.gameViewModel.popupHidden();
                this.variableSaver.saveBoolean(AuthConstants.WITH_PROGRESS, true);
            }
        }
        catch (error: unknown) {
            this.logger.error(`[GameController] Exception on firstShowMainLoginPopup: `, error);
            await this.reconnect();
        }
    }
}
